import React, { useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { toast } from 'react-hot-toast';
import type { Provider } from '@supabase/supabase-js';

interface SocialLoginProps {
  mode?: 'login' | 'register';
  invitation?: any;
  className?: string;
}

export const SocialLogin: React.FC<SocialLoginProps> = ({ 
  mode = 'login', 
  invitation, 
  className = '' 
}) => {
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
  const { signInWithProvider } = useAuthStore();

  const handleSocialLogin = async (provider: Provider) => {
    setLoadingProvider(provider);
    
    try {
      await signInWithProvider(provider);
      // The user will be redirected to the OAuth provider, so we don't need to do anything else here
    } catch (error: any) {
      console.error(`Error with ${provider} login:`, error);
      toast.error(`Failed to ${mode === 'login' ? 'sign in' : 'sign up'} with ${provider.charAt(0).toUpperCase() + provider.slice(1)}`);
      setLoadingProvider(null);
    }
  };

  const socialProviders = [
    {
      name: 'Google',
      provider: 'google' as Provider,
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      ),
      bgColor: 'bg-white hover:bg-gray-50',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-300'
    },
    {
      name: 'Slack',
      provider: 'slack_oidc' as Provider,
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
        </svg>
      ),
      bgColor: 'bg-purple-600 hover:bg-purple-700',
      textColor: 'text-white',
      borderColor: 'border-purple-600'
    }
  ];

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">
            Or {mode === 'login' ? 'sign in' : 'sign up'} with
          </span>
        </div>
      </div>

      <div className="space-y-2">
        {socialProviders.map((provider) => (
          <button
            key={provider.provider}
            type="button"
            disabled={loadingProvider === provider.provider}
            onClick={() => handleSocialLogin(provider.provider)}
            className={`
              w-full flex items-center justify-center px-4 py-2 border rounded-xl text-sm font-medium
              transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed
              ${provider.bgColor} ${provider.textColor} ${provider.borderColor}
            `}
          >
            {loadingProvider === provider.provider ? (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                {provider.icon}
                <span className="ml-2">
                  {mode === 'login' ? 'Sign in' : 'Sign up'} with {provider.name}
                </span>
              </>
            )}
          </button>
        ))}
      </div>
      
      {invitation && (
        <p className="text-xs text-gray-500 text-center mt-3">
          Using social login will automatically join you to {invitation.team?.name || invitation.teams?.name} as a{' '}
          {invitation.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach'}
        </p>
      )}
    </div>
  );
};

export default SocialLogin; 